// useFormLogic.jsx
import { useCallback, useEffect, useReducer } from 'react';

import { usePageNavigation } from '@src/context/PageNavigationContext';
import { useGenericSubmissionMutation } from '@src/query/useGenericSubmissionMutation';

/**
 * Initial state for the form reducer.
 */
const initialFormState = (initialFormData) => ({
  // Form Data & Validation
  formData: initialFormData,
  errors: {},

  // UI/Progress Status
  currentStep: 0, // 0: Form, 1: Success
  submissionStatus: 'idle', // 'idle', 'submitting', 'success', 'error'
  submissionError: null, // General message from server/mutation

  // ReCAPTCHA State
  recaptchaError: false,
  recaptchaResetFunc: null,
});

/**
 * Reducer function for managing all form-related state.
 */
function formReducer(state, action) {
  switch (action.type) {
    case 'CLEAR_ERROR':
      if (state.errors[action.field]) {
        return {
          ...state,
          errors: {
            ...state.errors,
            [action.field]: undefined,
          },
        };
      }
      return state;
    case 'HANDLE_INPUT_CHANGE':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.value,
        },
      };
    case 'RESET_FORM':
      return initialFormState(state.formData);
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.step,
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.errors,
      };
    case 'SET_RECAPTCHA_ERROR':
      return {
        ...state,
        recaptchaError: action.value,
      };
    case 'SET_RECAPTCHA_RESET_FUNC':
      return {
        ...state,
        recaptchaResetFunc: action.func,
      };
    case 'SET_SUBMISSION_ERROR':
      return {
        ...state,
        submissionError: action.message,
      };
    case 'SET_SUBMISSION_STATUS':
      return {
        ...state,
        submissionStatus: action.status,
      };
    default:
      return state;
  }
}

/**
 * A custom hook to handle the core logic (state, input, validation, mutation) for
 * form components that submit data via a POST request.
 */
export function useFormLogic(initialFormData, endpointPath, useValidationHook, csrfHookInfo, nextStep = null, scrollToTop = false) {

  // --- State Management with useReducer ---
  const [state, dispatch] = useReducer(formReducer, initialFormState(initialFormData));
  const {
    formData,
    errors,
    currentStep,
    recaptchaError,
    recaptchaResetFunc,
    submissionError,
    submissionStatus,
  } = state;

  // --- Listen to page navigation for reset ---
  const { navigationKey } = usePageNavigation();

  // Reset currentStep when navigation occurs to the same page
  useEffect(() => {
    if (navigationKey > 0 && currentStep !== 0) {
      dispatch({ type: 'SET_CURRENT_STEP', step: 0 });
    }
  }, [navigationKey, currentStep]);

  // --- Dispatch Wrappers ---
  const setRecaptchaError = useCallback((value) => dispatch({ type: 'SET_RECAPTCHA_ERROR', value }), [dispatch]);

  const setSubmissionError = useCallback((message) => dispatch({ type: 'SET_SUBMISSION_ERROR', message }), [dispatch]);

  const setCurrentStep = useCallback((step) => dispatch({ type: 'SET_CURRENT_STEP', step }), [dispatch]);

  const setSubmissionStatus = useCallback((status) => dispatch({ type: 'SET_SUBMISSION_STATUS', status }), [dispatch]);

  const setRecaptchaResetFunc = useCallback((func) => dispatch({ type: 'SET_RECAPTCHA_RESET_FUNC', func }), [dispatch]);


  // --- Input Handler ---
  const handleInputChange = useCallback((field, value) => {
    dispatch({ type: 'HANDLE_INPUT_CHANGE', field, value });
  }, []);

  // --- Clear Error Handler ---
  const clearError = useCallback((field) => {
    dispatch({ type: 'CLEAR_ERROR', field });
  }, []);

  // --- Mutation Hook Call ---
  const mutation = useGenericSubmissionMutation({
    endpointPath: endpointPath,
    setRecaptchaError: setRecaptchaError,
    setSubmissionError: setSubmissionError,
    setCurrentStep: setCurrentStep,
    nextStep: nextStep,
    scrollToTop: scrollToTop,
  });

  // --- Submission Handler (Unified Logic) ---
  const handleOnSubmit = useCallback(() => {
    setSubmissionStatus('submitting');

    const payload = {
      ...formData,
      recaptchaToken: formData.recaptchaToken,
    };

    mutation.mutate({
      payload: payload,
      csrfToken: csrfHookInfo.csrfToken,
      resetRecaptcha: recaptchaResetFunc,
    });
  }, [formData, csrfHookInfo.csrfToken, mutation, recaptchaResetFunc, setSubmissionStatus]);

  // --- Validation Hook Call ---
  // Pass the function to update the reducer's errors state.
  const { handleSubmit } = useValidationHook(
    formData,
    handleOnSubmit,
    (newErrors) => dispatch({ type: 'SET_ERRORS', errors: newErrors }), // The reducer's error setter
    currentStep,
  );

  // --- Return Values ---
  return {
    // State (From reducer)
    currentStep,
    recaptchaError,
    recaptchaResetFunc,
    submissionError,
    formData,
    submissionStatus,
    errors,

    // Handlers & Mutation (Dispatch Wrappers/Callbacks)
    handleInputChange,
    setRecaptchaResetFunc,
    setRecaptchaError,
    setSubmissionStatus,
    mutation,

    // Validation/Submission artifacts
    handleSubmit,
    clearError,
  };
}
